import React, { useEffect, useRef, useState } from "react";

/**
 * Full "Number of X" field: a title/description, a bordered "Quick select" box of preset circle
 * pills (plus a pencil icon that jumps focus to manual entry), an "OR" divider, a bordered
 * "Enter manually" number input, and a Min/Max caption underneath. Shared by team count and round
 * count so both pickers always look and behave identically.
 */
function CountField({ title, description, value, onChange, options, min, max, unitLabel, extraHint }) {
  const [draft, setDraft] = useState(String(value));
  const inputRef = useRef(null);

  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const handleChange = (raw) => {
    setDraft(raw);
    const n = parseInt(raw, 10);
    if (Number.isFinite(n) && n >= min && n <= max && String(n) === raw.trim()) {
      onChange(n);
    }
  };

  const finalize = () => {
    const n = parseInt(draft, 10);
    if (!Number.isFinite(n)) {
      setDraft(String(value));
      return;
    }
    const clamped = Math.min(max, Math.max(min, n));
    setDraft(String(clamped));
    if (clamped !== value) onChange(clamped);
  };

  const isCustomValue = !options.includes(value);
  // Quick-select presets are a fixed list (e.g. [4, 5, 6, 8] for rounds) that doesn't know about a
  // caller-specific max (e.g. capped to however many checkpoints exist) — offering a preset above
  // that cap would let a click silently blow right past it. Only ever show presets that actually
  // fit the current [min, max] window.
  const visibleOptions = options.filter((n) => n >= min && n <= max);

  return (
    <div className="studio-field">
      {title && <label>{title}</label>}
      {description && <div className="studio-hint studio-count-desc">{description}</div>}

      <div className="studio-count-row">
        <div className="studio-count-box">
          <div className="studio-count-box-label">Quick select</div>
          <div className="studio-count-pills">
            {visibleOptions.map((n) => (
              <button
                key={n}
                type="button"
                className={"studio-count-circle" + (value === n ? " studio-count-circle--active" : "")}
                onClick={() => {
                  setDraft(String(n));
                  onChange(n);
                }}
              >
                {n}
              </button>
            ))}
            <button
              type="button"
              className="studio-count-circle studio-count-circle--edit"
              title="Enter a custom count"
              onClick={() => inputRef.current?.focus()}
            >
              <i className="fa-solid fa-pen" />
            </button>
          </div>
        </div>

        <div className="studio-count-or">OR</div>

        <div className="studio-count-box">
          <div className="studio-count-box-label">Enter manually</div>
          <div className={"studio-count-manual" + (isCustomValue ? " studio-count-manual--active" : "")}>
            <input
              ref={inputRef}
              type="number"
              min={min}
              max={max}
              value={draft}
              onChange={(e) => handleChange(e.target.value)}
              onBlur={finalize}
              onKeyDown={(e) => {
                if (e.key === "Enter") finalize();
              }}
              aria-label={`Custom count (${min}-${max})`}
            />
          </div>
        </div>
      </div>

      <div className="studio-hint studio-count-caption">
        Min {min} {unitLabel} • Max {max} {unitLabel}
      </div>
      {extraHint && <div className="studio-hint">{extraHint}</div>}
    </div>
  );
}

export default CountField;
